import React, { createContext, useContext, useState } from 'react';
import { Player } from '../types';
import { supabase } from '../lib/supabase';

interface PlayerAddContextType {
  addPlayer: (memberId: string, tournamentId: string) => Promise<void>;
  isAdding: boolean;
  error: string | null; 
  initialPlayers: Player[];
  onPlayersUpdate: (players: Player[]) => void;
}

const PlayerAddContext = createContext<PlayerAddContextType | undefined>(undefined);

export function usePlayerAdd() {
  const context = useContext(PlayerAddContext);
  if (!context) {
    throw new Error('usePlayerAdd must be used within PlayerAddProvider');
  }
  return context;
}

export function usePlayerAddContext() {
  const context = useContext(PlayerAddContext);
  if (!context) {
    throw new Error('usePlayerAddContext must be used within PlayerAddProvider');
  }
  return {
    initialPlayers: context.initialPlayers,
    onPlayersUpdate: context.onPlayersUpdate
  };
}

interface PlayerAddProviderProps {
  children: React.ReactNode;
  onPlayersUpdate: (players: Player[]) => void;
  initialPlayers: Player[];
}

export function PlayerAddProvider({ 
  children, 
  onPlayersUpdate,
  initialPlayers 
}: PlayerAddProviderProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addPlayer = async (memberId: string, tournamentId: string) => {
    setIsAdding(true);
    setError(null);

    try {
      // First check if member exists
      const { data: member, error: searchError } = await supabase
        .from('members')
        .select('id, name, is_senior, season')
        .eq('id', memberId)
        .single();

      if (searchError && !searchError.message.includes('No rows found')) {
        throw searchError;
      }

      if (!member) {
        throw new Error('Member not found');
      }

      // Add player to tournament
      const newPlayer: Player = {
        id: member.id,
        name: member.name,
        tournamentId,
        handicap: member.season?.['2025']?.handicap || 0,
        flight: member.season?.['2025']?.flight || 'A',
        division: member.is_senior ? 'senior' : 'regular',
        scores: [],
        totalScore: 0
      };

      // Update local state
      const updatedPlayers = [...initialPlayers, newPlayer];
      onPlayersUpdate(updatedPlayers);

      // Sync with Supabase
      const { error: playerError } = await supabase
        .from('players')
        .insert([{
          id: newPlayer.id,
          name: newPlayer.name,
          tournament_id: newPlayer.tournamentId,
          handicap: newPlayer.handicap,
          flight: newPlayer.flight,
          scores: newPlayer.scores,
          total_score: newPlayer.totalScore
        }]);

      if (playerError) throw playerError;

    } catch (error) {
      console.error('Failed to add player:', error);
      setError(error instanceof Error ? error.message : 'Failed to add player');
      throw error;
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <PlayerAddContext.Provider value={{
      addPlayer,
      isAdding,
      error,
      initialPlayers,
      onPlayersUpdate
    }}>
      {children}
    </PlayerAddContext.Provider>
  );
}